// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Event log — a live, categorized trail of what the sync is doing, so the
 * privacy story is observable rather than merely asserted. Modelled on
 * Dropwell's drawer, with an in-drawer close button and Escape handled by the UI.
 */
import type { LogEntry } from './types.ts';
import { fmtClock } from './format.ts';

const MAX_EVENTS = 800;

let events: LogEntry[] = [];
let listeners: Array<(e: LogEntry) => void> = [];
let listEl: HTMLElement | null = null;
let countEl: HTMLElement | null = null;
let autoScroll = true;

export function emit(kind: LogEntry['kind'], text: string): void {
  const e: LogEntry = { ts: Date.now(), kind, text };
  events.push(e);
  if (events.length > MAX_EVENTS) {
    events.shift();
    if (listEl) listEl.querySelector('.event')?.remove();
  }
  for (const l of listeners) l(e);
}

export function clearLog(): void {
  events = [];
  if (listEl) listEl.innerHTML = '';
  if (countEl) countEl.textContent = '0';
}

export function mountEventDrawer(container: HTMLElement): () => void {
  container.innerHTML = '';

  const head = document.createElement('div');
  head.className = 'drawer-head';
  head.innerHTML = `
    <div class="drawer-title">event trail</div>
    <div class="drawer-controls">
      <span class="count"><strong id="ev-count">0</strong>&nbsp;events</span>
      <button type="button" class="filter-pill" id="ev-clear">clear</button>
      <button type="button" class="drawer-close" id="drawer-close" aria-label="Close event log">&times;</button>
    </div>`;
  container.appendChild(head);

  const list = document.createElement('div');
  list.className = 'drawer-list';
  container.appendChild(list);

  listEl = list;
  countEl = container.querySelector('#ev-count') as HTMLElement;
  (container.querySelector('#ev-clear') as HTMLElement).addEventListener('click', () => clearLog());

  list.addEventListener('scroll', () => {
    autoScroll = list.scrollTop + list.clientHeight >= list.scrollHeight - 32;
  });

  reflow();
  const onEvent = (e: LogEntry) => {
    appendEvent(e);
    if (countEl) countEl.textContent = String(events.length);
  };
  listeners.push(onEvent);
  return () => {
    listeners = listeners.filter((l) => l !== onEvent);
    listEl = null;
    countEl = null;
  };
}

function reflow(): void {
  if (!listEl) return;
  listEl.innerHTML = '';
  for (const e of events) appendEvent(e, false);
  listEl.scrollTop = listEl.scrollHeight;
  if (countEl) countEl.textContent = String(events.length);
}

function appendEvent(e: LogEntry, scroll = true): void {
  if (!listEl) return;
  const row = document.createElement('div');
  row.className = 'event';
  row.dataset.kind = e.kind;

  const ts = document.createElement('span');
  ts.className = 'ts';
  ts.textContent = fmtClock(new Date(e.ts));
  const dot = document.createElement('span');
  dot.className = 'ev-dot';
  const msg = document.createElement('span');
  msg.className = 'msg';
  msg.textContent = e.text;
  row.append(ts, dot, msg);

  listEl.appendChild(row);
  if (scroll && autoScroll) listEl.scrollTop = listEl.scrollHeight;
}
